"use client";

import { useId, useState, type ReactNode } from "react";

export interface TabItem {
  id: string;
  label: ReactNode;
  content?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  /** Controlled active tab id; omit for uncontrolled with `defaultValue`. */
  value?: string;
  defaultValue?: string;
  onChange?: (id: string) => void;
  className?: string;
}

export function Tabs({ items, value, defaultValue, onChange, className }: TabsProps) {
  const instanceId = useId();
  const [internal, setInternal] = useState(defaultValue ?? items[0]?.id);
  const active = value ?? internal;
  const activeItem = items.find((t) => t.id === active);

  return (
    <div className={className}>
      <div role="tablist" className="pz-tabs__list">
        {items.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`${instanceId}-tab-${t.id}`}
            aria-selected={t.id === active}
            aria-controls={`${instanceId}-panel-${t.id}`}
            className="pz-tab"
            disabled={t.disabled}
            onClick={() => {
              setInternal(t.id);
              onChange?.(t.id);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {activeItem?.content != null && (
        <div
          role="tabpanel"
          id={`${instanceId}-panel-${active}`}
          aria-labelledby={`${instanceId}-tab-${active}`}
          className="pz-tabs__panel"
        >
          {activeItem.content}
        </div>
      )}
    </div>
  );
}
